import { useState, useRef, useEffect } from 'react';
import { Bell, Check } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { remindersApi } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import type { Reminder } from '@imza/shared';

const COUNT_KEY = ['reminders', 'count'] as const;
const UNREAD_KEY = ['reminders', 'list', 'unread'] as const;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  // Poll every 60s; refetch in background while window is open. The badge
  // count is the only thing we ever fetch automatically — the list itself
  // is fetched lazily when the popover opens.
  const { data: countData } = useQuery({
    queryKey: COUNT_KEY,
    queryFn: () => remindersApi.count(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const { data: listData } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => remindersApi.list('unread', 50),
    enabled: open,
    staleTime: 30_000,
  });

  const markDone = useMutation({
    mutationFn: (id: number) =>
      remindersApi.patch(id, { done_at: new Date().toISOString() }),
    onMutate: async (id) => {
      // Optimistic: drop the row from the list, decrement the count.
      await queryClient.cancelQueries({ queryKey: UNREAD_KEY });
      await queryClient.cancelQueries({ queryKey: COUNT_KEY });

      const prevList = queryClient.getQueryData<{ items: Reminder[] }>(UNREAD_KEY);
      const prevCount = queryClient.getQueryData<{ unread: number }>(COUNT_KEY);

      if (prevList) {
        queryClient.setQueryData<{ items: Reminder[] }>(UNREAD_KEY, {
          items: prevList.items.filter((r) => r.id !== id),
        });
      }
      if (prevCount) {
        queryClient.setQueryData<{ unread: number }>(COUNT_KEY, {
          unread: Math.max(0, prevCount.unread - 1),
        });
      }
      return { prevList, prevCount };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prevList) queryClient.setQueryData(UNREAD_KEY, ctx.prevList);
      if (ctx?.prevCount) queryClient.setQueryData(COUNT_KEY, ctx.prevCount);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: COUNT_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
    },
  });

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const unread = countData?.unread ?? 0;
  const items = listData?.items ?? [];

  return (
    <div ref={containerRef} className="relative">
      <button
        className="relative p-2 rounded-lg text-secondary hover:text-primary-text hover:bg-hover-bg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className={cn(
              'absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full',
              'bg-danger text-white text-[10px] font-semibold',
              'flex items-center justify-center leading-none'
            )}
            aria-hidden="true"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 bg-white border border-border rounded-xl shadow-lg w-80 max-w-[calc(100vw-1rem)] overflow-hidden"
          role="dialog"
          aria-label="Reminders"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <p className="text-sm font-semibold text-primary-text">Reminders</p>
            {unread > 0 && (
              <span className="text-xs text-secondary">{unread} due</span>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 text-secondary mx-auto mb-2 opacity-40" />
                <p className="text-sm text-secondary">No reminders due</p>
                <p className="text-xs text-secondary mt-1 opacity-70">
                  You're all caught up.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {items.map((r) => (
                  <ReminderRow
                    key={r.id}
                    reminder={r}
                    onDone={() => markDone.mutate(r.id)}
                    pending={markDone.isPending && markDone.variables === r.id}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReminderRow({
  reminder,
  onDone,
  pending,
}: {
  reminder: Reminder;
  onDone: () => void;
  pending: boolean;
}) {
  const due = new Date(reminder.due_at);
  const relative = formatDistanceToNow(due, { addSuffix: true });

  return (
    <li className="px-4 py-3 hover:bg-hover-bg flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-primary-text break-words">
          {reminder.body ?? <span className="italic text-secondary">No description</span>}
        </p>
        <p className="text-xs text-secondary mt-1">{relative}</p>
      </div>
      <button
        onClick={onDone}
        disabled={pending}
        className={cn(
          'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium',
          'border border-border hover:bg-white hover:border-primary hover:text-primary',
          'transition-colors flex-shrink-0',
          pending && 'opacity-50 cursor-not-allowed'
        )}
        aria-label="Mark reminder as done"
      >
        <Check className="w-3 h-3" />
        Done
      </button>
    </li>
  );
}
